import { forwardRef, ComponentPropsWithoutRef } from "react";
import cx from "classnames";

import styles from "./Link.module.scss";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

/**
 * Props for the LinkButton component
 * @extends ComponentPropsWithoutRef<"button">
 * @extends LayoutUtilProps
 */
export type LinkButtonProps = Omit<
  ComponentPropsWithoutRef<"button">,
  "type" | "disabled"
> &
  LayoutUtilProps &
  (
    | {
        /**
         * The visual appearance of the LinkButton.
         * @default "primary"
         */
        appearance?: "primary";
        /**
         * Whether the LinkButton should have a quieter, less prominent appearance.
         * @remarks Only available when appearance is "primary".
         */
        quiet?: boolean;
      }
    | {
        /**
         * The visual appearance of the LinkButton. Secondary appearance cannot be quiet.
         */
        appearance: "secondary";
        /**
         * Whether the LinkButton should have a quieter, less prominent appearance.
         * @remarks Must be false when appearance is "secondary".
         */
        quiet?: false;
      }
  );

/**
 * LinkButton component for creating styled button elements that look like links.
 *
 * Features:
 * - Supports primary and secondary visual appearances
 * - Optional quiet mode for less prominent styling
 * - Fully accessible with proper ARIA support
 * - Supports all standard button attributes and events
 * - Includes layout utilities for positioning and spacing
 * - Customizable styling with className and style props
 * - Proper keyboard navigation support
 * - Data attributes for analytics tracking
 *
 * @example
 * <LinkButton onClick={() => console.log('clicked')}>
 *   Click me
 * </LinkButton>
 */
export const LinkButton = forwardRef<HTMLButtonElement, LinkButtonProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      className,
      appearance = "primary",
      quiet,
      style,
      ...rest
    } = componentProps;
    const linkClassNames = cx([styles["link"]], className, {
      [styles["quiet"]]: quiet,
      [styles["secondary"]]: appearance === "secondary",
    });

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    return (
      <button
        className={linkClassNames}
        data-anv="link-button"
        ref={ref}
        style={styleCombined}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

LinkButton.displayName = "LinkButton";
